// src/schedule/EmployeeSearchSelect.jsx
import React, { useMemo, useRef, useState, useEffect } from "react";
import PortalDropdown from "./PortalDropdown";
import Avatar from "./Avatar";

const asName = (e) => (typeof e === "string" ? e : e?.name || "");

/** Day-only: uznaj day tylko gdy html[data-theme="day"] i NIE ma klasy .dark */
function useIsDayTheme() {
    const get = () => {
        if (typeof document === "undefined") return false;
        const html = document.documentElement;
        return html.getAttribute("data-theme") === "day" && !html.classList.contains("dark");
    };
    const [isDay, setIsDay] = useState(get);
    useEffect(() => {
        if (typeof MutationObserver === "undefined") return;
        const html = document.documentElement;
        const obs = new MutationObserver(() => setIsDay(get()));
        obs.observe(html, { attributes: true, attributeFilter: ["class", "data-theme"] });
        return () => obs.disconnect();
    }, []);
    return isDay;
}

export default function EmployeeSearchSelect({
    employees = [],
    selectedEmployee = "",
    setSelectedEmployee,
    placeholder = "— Wszyscy —",
    panelAlign = "left",
    showTbdOption = false,
}) {
    const isDay = useIsDayTheme();
    const anchorRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");

    // zachowujemy kolejność z AdminPage; bez sortowania alfabetycznego
    const allItems = useMemo(() => {
        const uniq = new Map();
        employees.forEach((e) => {
            const name = asName(e);
            if (!name) return;
            if (!uniq.has(name)) uniq.set(name, typeof e === "object" ? e : { name });
        });
        return Array.from(uniq.values());
    }, [employees]);

    const filtered = useMemo(() => {
        if (!q) return allItems;
        const qq = q.toLowerCase().trim();
        return allItems.filter((e) => asName(e).toLowerCase().includes(qq));
    }, [allItems, q]);

    const currentLabel = selectedEmployee || placeholder;

    return (
        <div className="relative">
            {/* DARK — jak było; DAY — var(--btn-*) */}
            <button
                ref={anchorRef}
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                className={
                    isDay
                        ? "w-full flex items-center gap-2 rounded-lg px-3 py-2 border"
                        : "w-full flex items-center gap-2 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-gray-200"
                }
                style={
                    isDay
                        ? {
                            backgroundColor: "var(--btn-bg)",
                            color: "var(--btn-fg, var(--text))",
                            borderColor: "var(--btn-border, var(--border))",
                        }
                        : undefined
                }
            >
                {selectedEmployee ? (
                    <Avatar
                        employee={
                            allItems.find((e) => asName(e) === selectedEmployee) || {
                                name: selectedEmployee,
                            }
                        }
                        size={20}
                    />
                ) : (
                    <span className={isDay ? "" : "opacity-70"} style={isDay ? { opacity: 0.7 } : undefined}>
                        👥
                    </span>
                )}
                <span className={selectedEmployee ? "" : isDay ? "" : "opacity-70"} style={isDay && !selectedEmployee ? { opacity: 0.7 } : undefined}>
                    {currentLabel}
                </span>
            </button>

            <PortalDropdown
                anchorRef={anchorRef}
                open={open}
                onClose={() => setOpen(false)}
                align={panelAlign}
                width={320}
                maxHeight={360}
            >
                <div className="p-2 w-full">
                    {/* INPUT */}
                    <input
                        autoFocus
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Szukaj…"
                        className={
                            isDay
                                ? "w-full rounded px-2 py-1 mb-2 border outline-none"
                                : "w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-gray-200 mb-2"
                        }
                        style={
                            isDay
                                ? {
                                    backgroundColor: "var(--btn-bg)",
                                    color: "var(--btn-fg, var(--text))",
                                    borderColor: "var(--btn-border, var(--border))",
                                }
                                : undefined
                        }
                    />

                    {/* LISTA */}
                    <div
                        className={isDay ? "divide-y border-t border-b" : "divide-y divide-gray-700"}
                        style={isDay ? { borderColor: "var(--border)" } : undefined}
                    >
                        {showTbdOption && (
                            <button
                                onClick={() => {
                                    setSelectedEmployee("TBD");
                                    setOpen(false);
                                    setQ("");
                                }}
                                className={isDay ? "w-full flex items-center gap-2 px-2 py-2 text-left rounded" : "w-full flex items-center gap-2 px-2 py-2 hover:bg-gray-700 text-left"}
                                style={isDay ? { background: "transparent" } : undefined}
                                onMouseEnter={isDay ? (e) => (e.currentTarget.style.background = "rgba(0,0,0,0.04)") : undefined}
                                onMouseLeave={isDay ? (e) => (e.currentTarget.style.background = "transparent") : undefined}
                            >
                                <span className="inline-block w-[20px] text-center">🔴</span>
                                <span>TBD — wymagają uwagi</span>
                            </button>
                        )}

                        <button
                            onClick={() => {
                                setSelectedEmployee("");
                                setOpen(false);
                                setQ("");
                            }}
                            className={isDay ? "w-full flex items-center gap-2 px-2 py-2 text-left rounded" : "w-full flex items-center gap-2 px-2 py-2 hover:bg-gray-700 text-left"}
                            style={isDay ? { background: "transparent" } : undefined}
                            onMouseEnter={isDay ? (e) => (e.currentTarget.style.background = "rgba(0,0,0,0.04)") : undefined}
                            onMouseLeave={isDay ? (e) => (e.currentTarget.style.background = "transparent") : undefined}
                        >
                            <span className="inline-block w-[20px] text-center">—</span>
                            <span>{placeholder}</span>
                        </button>

                        {filtered.map((e, i) => (
                            <button
                                key={i}
                                onClick={() => {
                                    setSelectedEmployee(asName(e));
                                    setOpen(false);
                                    setQ("");
                                }}
                                className={isDay ? "w-full flex items-center gap-2 px-2 py-2 text-left rounded" : "w-full flex items-center gap-2 px-2 py-2 hover:bg-gray-700 text-left"}
                                style={isDay ? { background: "transparent" } : undefined}
                                onMouseEnter={isDay ? (ev) => (ev.currentTarget.style.background = "rgba(0,0,0,0.04)") : undefined}
                                onMouseLeave={isDay ? (ev) => (ev.currentTarget.style.background = "transparent") : undefined}
                            >
                                <Avatar employee={e} size={22} />
                                <span>{asName(e)}</span>
                            </button>
                        ))}

                        {!filtered.length && (
                            <div
                                className={isDay ? "px-2 py-3 text-sm" : "px-2 py-3 text-sm text-gray-400"}
                                style={isDay ? { color: "var(--muted)" } : undefined}
                            >
                                Brak wyników
                            </div>
                        )}
                    </div>
                </div>
            </PortalDropdown>
        </div>
    );
}
